import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Badge,
  DataTable,
  createColumnHeader,
  getInitials,
  type DataTableColumnDef
} from '@bliks-io/ui';
import type { PartnerLead } from '@bliks-io/contracts';
import { usePartnerLeads } from '@/pages/partner/api/queries/usePartnerLeads';
import { useHubspotLabel } from './hubspot-labels';
import { formatDate } from '@/lib/formatters';
import { NewLeadButton } from './NewLeadButton';
import { PartnerErrorState } from '@/pages/partner/components/PartnerErrorState';
import { PartnerPageHeader } from '@/pages/partner/components/PartnerPageHeader';
import { PartnerSalesTabsNav } from '@/pages/partner/components/PartnerSalesTabsNav';

function fullName(lead: PartnerLead): string {
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
  return name || '—';
}

/** Acquirer (partner employee) as a person card: avatar + name + role. */
function AcquirerCell({ acquirer }: { acquirer: PartnerLead['acquirer'] }) {
  if (!acquirer?.name) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-2">
      <Avatar className="w-7 h-7 border bg-background">
        <AvatarFallback className="text-3xs">{getInitials(acquirer.name)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-medium text-foreground">{acquirer.name}</span>
        {acquirer.jobTitle && (
          <span className="text-xs text-muted-foreground">{acquirer.jobTitle}</span>
        )}
      </div>
    </div>
  );
}

export function PartnerLeadsPage() {
  const { t, i18n } = useTranslation();
  const label = useHubspotLabel();
  const { data, isLoading, isError, error, isFetching, refetch } = usePartnerLeads();

  const leads = data?.leads ?? [];
  // partner_admin gets scope 'all' (every employee's leads) → show who acquired
  // each one. partner_staff gets 'mine', where the Akquisiteur is always
  // themselves, so the column would be redundant noise.
  const showAcquirer = data?.scope === 'all';

  const columns = useMemo<DataTableColumnDef<PartnerLead>[]>(() => {
    // Only partner_admin (scope 'all') sees mixed acquirers. Sits next to the
    // lead's qualification rather than up front, and renders as a person card.
    // For staff the acquirer is always themselves, so it is omitted.
    const acquirerColumn: DataTableColumnDef<PartnerLead> = {
      id: 'acquirer',
      accessorFn: (row) => row.acquirer?.name ?? '',
      header: createColumnHeader(t('partner.leads.columns.acquirer')),
      cell: ({ row }) => <AcquirerCell acquirer={row.original.acquirer} />
    };

    return [
      {
        id: 'name',
        accessorFn: (row) => fullName(row),
        header: createColumnHeader(t('partner.leads.columns.name')),
        cell: ({ row }) => <span className="font-medium">{fullName(row.original)}</span>
      },
      {
        accessorKey: 'email',
        header: createColumnHeader(t('partner.leads.columns.email')),
        cell: ({ row }) => row.original.email ?? '—'
      },
      {
        accessorKey: 'company',
        header: createColumnHeader(t('partner.leads.columns.company')),
        cell: ({ row }) => row.original.company ?? '—'
      },
      {
        id: 'relationship',
        accessorFn: (row) => (row.relationship ? label('relationship', row.relationship) : ''),
        header: createColumnHeader(t('partner.leads.columns.relationship')),
        cell: ({ row }) =>
          row.original.relationship ? label('relationship', row.original.relationship) : '—'
      },
      {
        id: 'lifecycleStage',
        accessorFn: (row) =>
          row.lifecycleStage ? label('lifecycleStage', row.lifecycleStage) : '',
        header: createColumnHeader(t('partner.leads.columns.lifecycleStage')),
        cell: ({ row }) =>
          row.original.lifecycleStage ? (
            <Badge variant="secondary">
              {label('lifecycleStage', row.original.lifecycleStage)}
            </Badge>
          ) : (
            '—'
          )
      },
      {
        id: 'leadStatus',
        accessorFn: (row) => (row.leadStatus ? label('leadStatus', row.leadStatus) : ''),
        header: createColumnHeader(t('partner.leads.columns.leadStatus')),
        cell: ({ row }) =>
          row.original.leadStatus ? (
            <Badge variant="outline">{label('leadStatus', row.original.leadStatus)}</Badge>
          ) : (
            '—'
          )
      },
      ...(showAcquirer ? [acquirerColumn] : []),
      {
        accessorKey: 'createdAt',
        header: createColumnHeader(t('partner.leads.columns.createdAt')),
        cell: ({ row }) => formatDate(row.original.createdAt ?? undefined, i18n.language)
      }
    ];
  }, [t, i18n.language, showAcquirer, label]);

  return (
    <div className="flex h-full flex-col">
      <PartnerSalesTabsNav />
      <div className="flex min-h-0 flex-1 flex-col gap-6 p-6">
        <PartnerPageHeader
          title={t('partner.leads.title')}
          subtitle={t('partner.leads.subtitle')}
          onRefresh={() => refetch()}
          isRefreshing={isFetching}
        >
          <NewLeadButton />
        </PartnerPageHeader>

        {isError ? (
          <PartnerErrorState
            error={error}
            icon={Users}
            title={t('partner.leads.notLinkedTitle')}
            description={t('partner.leads.notLinkedDescription')}
            onRetry={() => refetch()}
          />
        ) : (
          <DataTable
            columns={columns}
            data={leads}
            isLoading={isLoading}
            emptyMessage={t('partner.leads.empty')}
            enableGlobalFilter
          />
        )}
      </div>
    </div>
  );
}
